/**
 * Which build of the console this tab runs, and whether the server hands out a
 * newer one. Every deploy writes a new index.html naming its own hashed entry
 * script (`/assets/index-<hash>.js`). A tab opened before a deploy keeps running
 * the build it loaded until it is reloaded, so a link to a page only the new
 * build has lands on 404 — as a penalty's notification did on 2026-10-11, the
 * day penalties got a page of their own.
 */

const ENTRY = /\/assets\/index-[\w-]+\.js/;
const RELOADED_KEY = 'viganium.reloadedFor';

/** The entry script this tab runs; null in development, which has none. */
function runningBuild(): string | null {
  for (const script of Array.from(document.querySelectorAll<HTMLScriptElement>('script[type="module"][src]'))) {
    const entry = script.getAttribute('src')?.match(ENTRY);
    if (entry) return entry[0];
  }
  return null;
}

/** The entry script the server hands out now, asked past every cache; null when it cannot be told. */
async function servedBuild(): Promise<string | null> {
  try {
    const res = await fetch('/', { cache: 'no-store' });
    if (!res.ok) return null;
    return (await res.text()).match(ENTRY)?.[0] ?? null;
  } catch {
    return null;
  }
}

/**
 * A page this tab does not know may be one a newer build has: when the server
 * hands out another build than the one running, load it. Once per build and
 * address, so a page the new build lacks too still says so, and never loops.
 * True when the page is reloading.
 */
export async function reloadIfOutdated(): Promise<boolean> {
  const running = runningBuild();
  if (!running) return false;
  const served = await servedBuild();
  if (!served || served === running) return false;
  const mark = `${served} ${window.location.pathname}${window.location.search}`;
  try {
    if (sessionStorage.getItem(RELOADED_KEY) === mark) return false;
    sessionStorage.setItem(RELOADED_KEY, mark);
  } catch {
    // No storage (a private window that refuses it): reloading once is still right.
  }
  window.location.reload();
  return true;
}
